import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calculator, Gauge, TrendingUp, Shield } from "lucide-react";

const Tools = () => {
  const tools = [
    {
      title: "Tank Volume Calculator",
      description: "Calculate internal volume for cylindrical tanks with various head types including elliptical, torispherical, hemispherical, and flat heads.",
      features: [
        "Multiple head type configurations",
        "Precise volume calculations",
        "Capacity planning support",
        "Storage sizing optimization"
      ],
      useCases: [
        "Industrial storage design",
        "Process tank sizing",
        "Capacity verification",
        "System planning"
      ],
      icon: Calculator,
      path: "/tools/tank-volume-calculator",
      color: "text-blue-600"
    },
    {
      title: "Tank Weight Calculator", 
      description: "Estimate steel weight and structural loads for tank fabrication with accurate surface area calculations and material density factors.",
      features: [
        "Surface area-based calculations",
        "Multiple material options",
        "Load per leg distribution",
        "Safety factor integration"
      ],
      useCases: [
        "Structural design planning",
        "Foundation load calculations",
        "Material cost estimation",
        "Support design verification"
      ],
      icon: Gauge,
      path: "/tools/tank-weight-calculator", 
      color: "text-green-600"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Engineering Tools - Tank Calculators | WLE</title>
        <meta 
          name="description" 
          content="Professional engineering tools for tank design including volume and weight calculators. Accurate calculations for industrial tank fabrication and structural planning." 
        />
        <meta name="keywords" content="tank calculator, volume calculator, weight calculator, engineering tools, tank design, industrial fabrication" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="bg-gradient-to-b from-primary/5 to-background py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Engineering Tools & Calculators
                </h1>
                <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                  Professional-grade calculation tools designed for engineers, designers, and contractors. 
                  Get accurate preliminary calculations for tank design, fabrication planning, and structural analysis.
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span>Precision Engineering</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>Industry Standards</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-primary" />
                    <span>Instant Results</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Tools Grid */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {tools.map((tool, index) => {
                  const IconComponent = tool.icon;
                  return (
                    <Card key={index} className="group hover:shadow-lg transition-shadow duration-300 border-border/50">
                      <CardHeader className="pb-4">
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg bg-primary/10 ${tool.color}`}>
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-2xl mb-2 group-hover:text-primary transition-colors">
                              {tool.title}
                            </CardTitle>
                            <CardDescription className="text-base leading-relaxed">
                              {tool.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-foreground mb-3">Key Features:</h4>
                          <ul className="space-y-2">
                            {tool.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-foreground mb-3">Use Cases:</h4>
                          <ul className="space-y-2">
                            {tool.useCases.map((useCase, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                                <span>{useCase}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="pt-4 border-t border-border/50">
                          <Button asChild className="w-full">
                            <Link to={tool.path}>
                              Use {tool.title}
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Professional Disclaimer */}
          <section className="py-12 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h3 className="text-2xl font-semibold text-foreground mb-4">
                  Professional Engineering Consultation
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  These tools provide accurate preliminary calculations for design and planning purposes. 
                  For final specifications, detailed engineering analysis, and professional certifications, 
                  we recommend consulting with our experienced engineering team.
                </p>
                <Button asChild variant="outline">
                  <Link to="/contact">
                    Contact Our Engineers
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Tools;